import { useState } from 'react'
import { Search, Github, Star, GitFork, Code, Download, ExternalLink, BookOpen, Loader2 } from 'lucide-react'

export default function CodebaseDiscovery() {
  const [query, setQuery] = useState('')
  const [repos, setRepos] = useState([])
  const [loading, setLoading] = useState(false)
  const [ingesting, setIngesting] = useState(null)
  const [ingested, setIngested] = useState([])
  const [error, setError] = useState('')

  const searchRepos = async () => {
    if (!query.trim()) return
    setLoading(true)
    setError('')
    try {
      const res = await fetch(`https://api.github.com/search/repositories?q=${encodeURIComponent(query)}&sort=stars&order=desc&per_page=12`)
      if (!res.ok) throw new Error(`GitHub API: ${res.status}`)
      const data = await res.json()
      setRepos(data.items || [])
    } catch (e) {
      setError(e.message)
      setRepos([])
    }
    setLoading(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') searchRepos()
  }

  const ingestRepo = async (repo) => {
    setIngesting(repo.full_name)
    try {
      // Fetch README
      const readmeRes = await fetch(`https://api.github.com/repos/${repo.full_name}/readme`)
      let readmeContent = ''
      if (readmeRes.ok) {
        const readmeData = await readmeRes.json()
        readmeContent = atob(readmeData.content || '')
      }

      // Fetch repo tree (root level only for speed)
      const treeRes = await fetch(`https://api.github.com/repos/${repo.full_name}/git/trees/${repo.default_branch}?recursive=1`)
      let fileTree = []
      if (treeRes.ok) {
        const treeData = await treeRes.json()
        fileTree = (treeData.tree || []).slice(0, 50) // Limit to 50 files
      }

      const ingestion = {
        id: repo.id,
        name: repo.full_name,
        url: repo.html_url,
        stars: repo.stargazers_count,
        language: repo.language,
        description: repo.description,
        readme: readmeContent.slice(0, 8000), // Truncate for storage
        fileTree: fileTree.map(f => f.path).slice(0, 30),
        ingestedAt: new Date().toISOString()
      }

      setIngested(prev => [ingestion, ...prev.filter(i => i.id !== repo.id)])
    } catch (e) {
      console.error('Ingestion failed:', e)
    }
    setIngesting(null)
  }

  const formatStars = (n) => {
    if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
    return n
  }

  const copyIngestion = (ing) => {
    const text = `Repository: ${ing.name}
URL: ${ing.url}
Stars: ${ing.stars}
Language: ${ing.language}
Description: ${ing.description}

=== README ===
${ing.readme}

=== FILE TREE ===
${ing.fileTree.join('\n')}
`
    navigator.clipboard.writeText(text)
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="panel">
        <h3 className="text-xs font-bold text-gray-400 mb-3 flex items-center gap-2">
          <Github className="w-3.5 h-3.5" />
          GitHub Codebase Discovery
        </h3>
        <p className="text-[10px] text-gray-500 mb-3">
          Search GitHub repositories. Ingest them for agent analysis. Powered by GitHub Search API.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search repos... (e.g., 'llama.cpp', 'qwen', 'tars')"
            className="flex-1 bg-aith-panel border border-aith-border rounded-lg px-3 py-2 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-aith-accent"
          />
          <button
            onClick={searchRepos}
            disabled={loading || !query.trim()}
            className="px-4 py-2 bg-aith-accent text-white rounded-lg hover:bg-blue-600 disabled:opacity-40 transition-colors flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            <span className="text-xs">Search</span>
          </button>
        </div>
        {error && <p className="text-[10px] text-aith-red mt-2">{error}</p>}
      </div>

      {/* Results */}
      {repos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {repos.map(repo => (
            <div key={repo.id} className="panel hover:border-aith-accent/30 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Github className="w-3.5 h-3.5 text-gray-400" />
                  <a
                    href={repo.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-aith-accent hover:underline flex items-center gap-1"
                  >
                    {repo.full_name}
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-gray-500">
                  <Star className="w-3 h-3 text-aith-yellow" />
                  {formatStars(repo.stargazers_count)}
                </div>
              </div>

              <p className="text-[10px] text-gray-400 mb-2 line-clamp-2">{repo.description}</p>

              <div className="flex items-center gap-3 text-[10px] text-gray-500 mb-3">
                {repo.language && (
                  <span className="flex items-center gap-1">
                    <Code className="w-2.5 h-2.5" />
                    {repo.language}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <GitFork className="w-2.5 h-2.5" />
                  {formatStars(repo.forks_count)}
                </span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => ingestRepo(repo)}
                  disabled={ingesting === repo.full_name}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-aith-purple/10 border border-aith-purple/20 text-aith-purple rounded text-[10px] font-bold hover:bg-aith-purple/20 transition-colors disabled:opacity-40"
                >
                  {ingesting === repo.full_name ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3" />
                  )}
                  {ingesting === repo.full_name ? 'Ingesting...' : 'Ingest'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ingested */}
      {ingested.length > 0 && (
        <div className="panel">
          <h3 className="text-xs font-bold text-gray-400 mb-3 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" />
            Ingested Codebases ({ingested.length})
          </h3>
          <div className="space-y-2">
            {ingested.map(ing => (
              <div key={ing.id} className="bg-aith-panel border border-aith-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-gray-300">{ing.name}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => copyIngestion(ing)}
                      className="px-2 py-1 text-[9px] bg-aith-accent/10 text-aith-accent rounded hover:bg-aith-accent/20 transition-colors"
                    >
                      Copy Context
                    </button>
                  </div>
                </div>
                <p className="text-[10px] text-gray-500 mb-1">{ing.description}</p>
                <div className="flex items-center gap-3 text-[9px] text-gray-600">
                  <span>{ing.stars} stars</span>
                  <span>{ing.language}</span>
                  <span>{ing.fileTree.length} files indexed</span>
                  <span>{ing.readme.length} chars README</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
